import { useQuery, type ApolloError } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { DsrcDeclaration } from '../../../../DRCAssessment/components';
import { type GSAssigneeList } from '../../../GSAssessment';
import { type SelectOption } from '../../../../../../components';

type GetDsrcDeclaration = {
  dsrcDeclaration: DsrcDeclaration;
  gsAssigneeOption: SelectOption[];
  stipendBudgetDeclarationRequired: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const useGetDsrcDeclaration = (
  applicationId: string,
): GetDsrcDeclaration => {
  const { data, loading, error, refetch } = useQuery(
    gql`
      query (
        $assessmentFilter: AssessmentFilter!
        $nominationFilter: NominationFilter!
      ) {
        dsrcDeclaration(filter: $assessmentFilter) {
          proof
          recommendThreshold
          stipendBudgetReserved
          PIofStem
          PIofGRF
          remarks
        }
        getGSAssigneeList {
          assigneeList {
            netId
            displayName
          }
        }

        nominations(filter: $nominationFilter) {
          fundings {
            fundingPlan {
              accountCode
            }
          }
        }
      }
    `,
    {
      variables: {
        assessmentFilter: { applicationId },
        nominationFilter: {
          application: { _id: { eq: applicationId } },
        },
      },
      fetchPolicy: 'network-only',
      skip: !applicationId,
    },
  );

  const dsrcDeclarationData: DsrcDeclaration = data?.dsrcDeclaration ?? {};
  const fundingsInfo = data?.nominations?.[0]?.fundings ?? [];

  // When applicants' nominated funding has FundingPlan and FundingPlan.accountCode is not empty
  // need to add extra declaration. (06/03/2025)
  // here uses stipendBudgetDeclarationRequired as a flag

  // TODO to be enhanced, `not empty` for some or every?
  // Concern: If the user does not tick the sufficient funding checkbox,
  // the front end will not impose any restrictions on the funding plan, including whether the dates cover 12 consecutive months
  const stipendBudgetDeclarationRequired: boolean = fundingsInfo.some(
    (f: any) =>
      (f?.fundingPlan ?? [])?.length &&
      (f?.fundingPlan ?? [])?.some((p: any) => Boolean(p?.accountCode)),
  );

  const {
    proof,
    recommendThreshold,
    stipendBudgetReserved,
    remarks,
    PIofStem,
    PIofGRF,
  } = dsrcDeclarationData ?? {};

  const dsrcDeclaration: DsrcDeclaration = {
    proof: proof ?? false,
    recommendThreshold: recommendThreshold ?? false,
    stipendBudgetReserved: stipendBudgetReserved ?? false,
    PIofStem,
    PIofGRF,
    remarks: remarks ?? '',
  };

  const gsAssigneeList = (data?.getGSAssigneeList?.assigneeList ??
    []) as GSAssigneeList[];

  const gsAssigneeOption: SelectOption[] = gsAssigneeList.map(assignee => ({
    value: assignee.netId,
    label: `${assignee.displayName} (${assignee.netId})`,
  }));

  return {
    dsrcDeclaration,
    gsAssigneeOption,
    stipendBudgetDeclarationRequired,
    loading,
    error,
    refetch,
  };
};
